import * as React from 'react'

import { UseFormReturn, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Flex,
  Grid,
  Separator,
  Stack,
  Text,
} from '@enechain/ecloud-designsystem'
import {
  DealPosition,
  ProductSpreadType,
} from '@enechain/eposition-proto/types/v1/deal_pb'
import { Card } from '@enechain/eposition-ui'

import { EditableSimulationTransactionProvider } from '~/pages/Simulation/components/OrderPatternInputArea/SummaryTransactionInputSection/EditableTransactionProvider'
import { SummaryTransactionForm } from '~/pages/Simulation/components/OrderPatternInputArea/SummaryTransactionInputSection/SummaryTransactionForm'
import { useListFloatingIndices } from '~/pages/Simulation/hooks/useListFloatingIndices'
import { useSyncSummaryOrder } from '~/pages/Simulation/hooks/useSyncSummaryOrder'
import { OrderSimulationSchema } from '~/pages/Simulation/schemas'

type Props = {
  formMethods: UseFormReturn<OrderSimulationSchema>
  index: number
}

export const SummaryTransactionInputSection: React.FC<Props> = ({
  formMethods,
  index,
}) => {
  const { t } = useTranslation(['page', 'domain', 'common'])

  const watchedIsSpreadTrade = useWatch({
    name: `orderPattern.${index}.spreadType.isSpreadTrade`,
    control: formMethods.control,
  })
  const watchedSpreadType = useWatch({
    name: `orderPattern.${index}.spreadType.spreadType`,
    control: formMethods.control,
  })
  const watchedDealPosition = useWatch({
    name: `orderPattern.${index}.dealPosition`,
    control: formMethods.control,
  })
  const watchedIsPhysicalFuture = useWatch({
    name: `orderPattern.${index}.isPhysicalFuture`,
    control: formMethods.control,
  })

  useSyncSummaryOrder({ formMethods, index })
  const floatingIndexOptions = useListFloatingIndices(!watchedIsPhysicalFuture)

  const resetLegs = (): void => {
    formMethods.setValue(`orderPattern.${index}.baseTransactions`, [])
    formMethods.setValue(`orderPattern.${index}.exchangeTransactions`, [])
    formMethods.clearErrors(`orderPattern.${index}.baseTransactions`)
    formMethods.clearErrors(`orderPattern.${index}.exchangeTransactions`)
    formMethods.trigger(`orderPattern.${index}.exchangeSummaryOrder`)
    formMethods.trigger(`orderPattern.${index}.baseSummaryOrder`)
  }

  return watchedIsSpreadTrade ? (
    <Grid gridGap="4" gridTemplateColumns="1fr 1fr">
      <Card>
        <Stack gap="4">
          <Flex gap="1" alignItems="center">
            <Text textStyle="headerSm">{t('page:simulation.base_side')}</Text>
          </Flex>
          <Separator />
          <SummaryTransactionForm
            control={formMethods.control}
            schemaKeyName={`orderPattern.${index}.baseSummaryOrder`}
            isPhysicalFuture={watchedIsPhysicalFuture}
            dealPosition={watchedDealPosition}
            resetLegs={resetLegs}
            floatingIndexOptions={floatingIndexOptions}
          />
        </Stack>
      </Card>
      <Card>
        <Stack gap="4">
          <Flex gap="1" alignItems="center">
            <Text textStyle="headerSm">
              {t('page:simulation.exchange_side')}
            </Text>
          </Flex>
          <Separator />
          <EditableSimulationTransactionProvider
            editableColumns={{
              areaCode:
                watchedSpreadType !== undefined &&
                watchedSpreadType.includes(ProductSpreadType.AREA),
              // ref: https://www.notion.so/enechain/eClear-EEX-1c231f880991803e894eda80b6b861cc?source=copy_link#1fe31f880991801a81f0f127aa14380c
              deliveryPeriodType:
                watchedSpreadType !== undefined &&
                watchedSpreadType.includes(ProductSpreadType.DELIVERY_PERIOD),
              deliveryDateRange:
                watchedSpreadType !== undefined &&
                watchedSpreadType.includes(ProductSpreadType.DELIVERY_PERIOD),
              deliveryHourType:
                watchedSpreadType !== undefined &&
                watchedSpreadType.includes(ProductSpreadType.HOUR_TYPE),
              // ref: https://www.notion.so/enechain/eClear-EEX-1c231f880991803e894eda80b6b861cc?source=copy_link#1fc31f880991804c9cc4d75f775876e5
              volumeInKw:
                watchedSpreadType !== undefined &&
                watchedSpreadType.includes(ProductSpreadType.HOUR_TYPE),
            }}
          >
            <SummaryTransactionForm
              control={formMethods.control}
              schemaKeyName={`orderPattern.${index}.exchangeSummaryOrder`}
              isPhysicalFuture={watchedIsPhysicalFuture}
              dealPosition={
                watchedDealPosition === DealPosition.BID
                  ? DealPosition.OFFER
                  : DealPosition.BID
              }
              resetLegs={resetLegs}
              floatingIndexOptions={floatingIndexOptions}
            />
          </EditableSimulationTransactionProvider>
        </Stack>
      </Card>
    </Grid>
  ) : (
    <Card>
      <SummaryTransactionForm
        isPhysicalFuture={watchedIsPhysicalFuture}
        control={formMethods.control}
        schemaKeyName={`orderPattern.${index}.baseSummaryOrder`}
        dealPosition={watchedDealPosition}
        resetLegs={resetLegs}
        floatingIndexOptions={floatingIndexOptions}
      />
    </Card>
  )
}
